import { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@osago/ui';
import dayjs from 'dayjs';

import { agreementService } from '../services/agreementService';
import { useSessionId } from '../hooks/useSessionId';
import { Agreement } from '../types/models/agreement';
import { ApiResult } from '../types/api/common';
import { AgreementResponse } from '../types/api/responses';
import mockData from '../mocks/agreements.json';

import Layout from '../components/layout';
import AgreementItem from '../components/agreementItem';
import AgreementsFilter, { FilterState } from '../components/agreementsFilter';
import { useInsurers } from '../hooks/useInsurers';
import { normalizeCompanyCode } from '../helpers/companyAliases';
import { getAgreementCompanyCode } from '../helpers/mortgage';
import { DOMAIN_NAME } from '../config/api';

const DEFAULT_FILTERS: FilterState = {
  dateFrom: '',
  dateTo: '',
  company: '',
  product: '',
  status: 'active',
};

const getVehicleKey = (agreement: Agreement): string => {
  const vin = agreement.dataObject?.vin?.trim();
  if (vin) return `vin:${vin.toUpperCase()}`;
  const plate = agreement.dataObject?.licensePlate?.trim();
  return plate ? `plate:${plate.toUpperCase()}` : '';
};

const getProductType = (agreement: Agreement): string => {
  if (agreement.policyType) return agreement.policyType;
  const lower = (agreement.type || '').toLowerCase();
  if (lower === 'kasko') return 'kasko';
  if (lower === 'pkasko') return 'pkasko';
  if (lower === 'pkasko_standalone') return 'pkasko_standalone';
  if (lower === 'dkasko') return 'dkasko';
  if (lower === 'accident') return 'accident';
  return 'osago';
};

const getMockAgreements = (): Agreement[] => {
  if (import.meta.env.MODE === 'production') return [];
  const params = new URLSearchParams(window.location.search);
  if (params.get('ff') !== 'mock') return [];
  return (mockData.data.agreementList as unknown as Agreement[]).map((a) => ({ ...a, isMock: true }));
};

const DashboardPage = () => {
  const [agreements, setAgreements] = useState<Agreement[]>(getMockAgreements);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const sessionId = useSessionId();
  const { insurers, getCompanyTitle } = useInsurers();

  const insurerByCode = useMemo(() => {
    const map: Record<string, typeof insurers[number]> = {};
    insurers.forEach((i) => {
      map[normalizeCompanyCode(i.code)] = i;
    });
    return map;
  }, [insurers]);

  const loadAgreements = useCallback(() => {
    setIsLoading(true);
    setLoadError(null);

    agreementService(sessionId).then(handleAgreementServiceResponse);
  }, [sessionId]);

  useEffect(() => {
    loadAgreements();
  }, [loadAgreements]);

  const handleAgreementServiceResponse = ([err, res]: ApiResult<AgreementResponse>) => {
    setIsLoading(false);

    if (err || !res) {
      setLoadError('Не удалось загрузить полисы. Проверьте соединение и попробуйте ещё раз.');
      return;
    }

    if (res.error) {
      setLoadError(res.message || 'Не удалось загрузить полисы. Попробуйте ещё раз.');
      return;
    }

    const { agreementList } = res.data || {};

    if (agreementList) {
      setAgreements((prev) => {
        const mocks = prev.filter((a) => a.isMock);
        return [...agreementList, ...mocks];
      });
    }
  };

  const availableCompanies = useMemo(
    () => [...new Set(agreements.map(getAgreementCompanyCode).filter(Boolean))],
    [agreements]
  );

  const expandedAgreements = useMemo(() =>
    agreements.flatMap((a) => {
      const main = { ...a, crossProducts: undefined };
      const cross = (a.crossProducts ?? []).map((cp) => ({
        ...a,
        id: cp.policyNumber || `${a.id}-${cp.type}`,
        policyType: cp.type as Agreement['policyType'],
        price: String(cp.price),
        crossProducts: undefined,
      }));
      return [main, ...cross];
    }),
    [agreements]
  );

  const latestEndByVehicle = useMemo(() => {
    const map: Record<string, string> = {};
    expandedAgreements.forEach((a) => {
      const key = getVehicleKey(a);
      if (!key || !a.ended) return;
      if (!map[key] || dayjs(a.ended).isAfter(dayjs(map[key]))) {
        map[key] = a.ended;
      }
    });
    return map;
  }, [expandedAgreements]);

  const hasNewerPolicy = (agreement: Agreement): boolean => {
    const key = getVehicleKey(agreement);
    if (!key || !agreement.ended) return false;
    const latest = latestEndByVehicle[key];
    return Boolean(latest && dayjs(latest).isAfter(dayjs(agreement.ended)));
  };

  const filteredAgreements = useMemo(() => {
    const today = dayjs();
    return expandedAgreements.filter((a) => {
      if (filters.status) {
        const isExpired = dayjs(a.ended).isBefore(today, 'day');
        if (filters.status === 'active' && isExpired) return false;
        if (filters.status === 'inactive' && !isExpired) return false;
      }
      if (filters.dateFrom && dayjs(a.started).isBefore(dayjs(filters.dateFrom), 'day')) {
        return false;
      }
      if (filters.dateTo && dayjs(a.ended).isAfter(dayjs(filters.dateTo), 'day')) {
        return false;
      }
      if (filters.company) {
        const companyLabel = getCompanyTitle(getAgreementCompanyCode(a));
        if (companyLabel !== filters.company) return false;
      }
      if (filters.product && getProductType(a) !== filters.product) {
        return false;
      }
      return true;
    });
  }, [expandedAgreements, filters, getCompanyTitle]);

  const renderAgreementsList = () => {
    if (loadError && !isLoading) {
      return (
        <div className="policy-list__empty">
          {loadError}
          <div className="mt-4">
            <Button variant="secondary" size="sm" onClick={loadAgreements}>
              Повторить
            </Button>
          </div>
        </div>
      );
    }

    if (agreements.length === 0 && !isLoading) {
      return (
        <div className="policy-list__empty">
          Получаем данные от страховой компании, это может занять от 5 до 30
          минут. <br /> Пожалуйста, подождите.
        </div>
      );
    }

    if (agreements.length === 0 && isLoading) {
      return <div className="policy-list__empty">Загрузка...</div>;
    }

    if (filteredAgreements.length === 0) {
      return (
        <div className="policy-list__empty">
          Нет полисов, соответствующих выбранным фильтрам.
        </div>
      );
    }

    return filteredAgreements.map((agreement) => (
      <AgreementItem
        key={`${agreement.id}-${agreement.policyType}-${agreement.company}`}
        {...agreement}
        insurer={insurerByCode[normalizeCompanyCode(getAgreementCompanyCode(agreement))]}
        hasNewerPolicy={hasNewerPolicy(agreement)}
      />
    ));
  };

  return (
    <Layout>
      <Helmet>
        <title>Личный кабинет | {DOMAIN_NAME}</title>
      </Helmet>
      <div className="policy-list">
        {agreements.length > 0 && (
          <AgreementsFilter
            filters={filters}
            availableCompanies={availableCompanies}
            getCompanyTitle={getCompanyTitle}
            onChange={setFilters}
            onReset={() => setFilters(DEFAULT_FILTERS)}
          />
        )}
        {renderAgreementsList()}
      </div>
    </Layout>
  );
};

export default DashboardPage;
